import React from "react";
import Button from "react-bootstrap/Button";

// how a table is sorted: by which column, and in which direction
export interface Sort<TKey extends string> {
  key: TKey;
  asc: boolean;
}

// the sort after clicking a column: the active column flips direction,
// another is sorted ascending
export const nextSort = <TKey extends string>(
  s: Sort<TKey>,
  key: TKey
): Sort<TKey> => ({ key, asc: s.key === key ? !s.asc : true });

// compares text ignoring case, with numbers by value ("#9" before "#10")
export const compareText = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

interface SortHeaderProps<TKey extends string> {
  label: string;
  sortKey: TKey;
  sort: Sort<TKey>;
  onSort: (key: TKey) => void;
}

// a table column header that sorts the table by its column when clicked,
// showing the direction when it is the column sorted by
export const SortHeader = <TKey extends string>({
  label,
  sortKey,
  sort,
  onSort,
}: SortHeaderProps<TKey>): JSX.Element => {
  const active = sort.key === sortKey;
  return (
    <Button
      variant="link"
      className="p-0 text-reset font-weight-bold text-decoration-none text-nowrap"
      onClick={(): void => onSort(sortKey)}
    >
      {label}
      {/* every header keeps room for the arrow, so the columns do not
          resize as it moves from one to another */}
      <span
        aria-hidden={!active}
        style={{ display: "inline-block", width: "1.25em", textAlign: "right" }}
      >
        {active && (sort.asc ? "▲" : "▼")}
      </span>
    </Button>
  );
};
